import {
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Spacer,
  Tag,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { formatToCurrency } from '@common/utils/formatter';
import isNil from 'lodash/isNil';
import { PencilIcon } from 'lucide-react';
import { useSharedPageContext } from '~/apps/consumer/contexts/SharedPageContext';
import { numberFormatter } from '~/common/utils/formatter';
import { PremiumTag } from '../../../PremiumTag/PremiumTag';
import { ListingAgentInformation } from '../../../Storefront/ListingAgent/ListingAgentCard';
import { ListingPhotoCarousel } from '../../ListingPhotoCarousel/ListingPhotoCarousel';
import { EditOffMarketListingModal } from '../../OffMarketListings/EditOffMarketListing/EditOffMarketListingModal';
import { useListingPageContext } from '../ListingPageContext';
import { getPropertyDetails } from '../utils';

export const ListingInfo = () => {
  const { isForPdf } = useSharedPageContext();
  const {
    listing,
    propertyDetails,
    isViewOnly,
    isMyListing,
    isPrint,
    refetchPropertyDetails,
    refetchListingReport,
  } = useListingPageContext();
  const {
    listPrice,
    photos,
    streetAddress,
    secondAddressLine,
    bedrooms,
    bathrooms,
    area,
    daysOnMarket,
    status,
  } = getPropertyDetails(listing, propertyDetails);
  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <Box px={isPrint ? 10 : { base: 0, md: 10 }} width="100%">
      {photos && (
        <ListingPhotoCarousel
          images={
            isForPdf
              ? photos?.slice(0, 2).map((photo) => `${photo}?d=s`)
              : photos?.map((photo) => `${photo}?d=m`)
          }
          options={
            isPrint ? { perPage: 2, breakpoints: {}, arrows: false } : undefined
          }
        />
      )}
      <Spacer h={photos ? 6 : 2} />
      {isMyListing && !isViewOnly && <PremiumTag label="My Listing" />}
      <Flex
        alignItems="center"
        direction="row"
        gap={2}
        justifyContent="space-between"
        pt={1}
      >
        <Flex alignItems="center" gap={4}>
          <Flex direction="column">
            <Flex alignItems="center" gap={2}>
              <Heading as="h2" fontWeight="semibold">
                {formatToCurrency(listPrice)}
              </Heading>
              <Tag
                bg="whiteAlpha.300"
                color="white"
                lineHeight={1}
                textTransform="none"
              >
                {status}
              </Tag>
              {!!propertyDetails && !isViewOnly && (
                <IconButton
                  aria-label="Edit off-market listing"
                  icon={<Icon as={PencilIcon} boxSize={4} color="white" />}
                  size="xs"
                  variant="ghost"
                  onClick={onOpen}
                />
              )}
            </Flex>
            <Heading as="h4" fontWeight="semibold" p={0} size="xxs" zIndex={1}>
              {streetAddress ?? ''}
              <Text as="span" fontWeight="400" p={0} size="xxs" zIndex={1}>
                {secondAddressLine ? ` ${secondAddressLine}` : ''}
              </Text>
            </Heading>
            <Flex gap={8} mt={0}>
              <Text color="whiteAlpha.700" fontSize="md">
                {!isNil(bedrooms) && `${bedrooms} beds`}
                {!isNil(bedrooms) && !isNil(bathrooms) && ' • '}
                {!isNil(bathrooms) && `${bathrooms} baths`}
                {(!isNil(bedrooms) || !isNil(bathrooms)) &&
                  !isNil(area) &&
                  ' • '}
                {!isNil(area) &&
                  `${numberFormatter(area, { maxPrecision: 0 })} sqft`}
                {(!isNil(bedrooms) || !isNil(bathrooms) || !isNil(area)) &&
                  !isNil(daysOnMarket) &&
                  ' • '}
                {!isNil(daysOnMarket) && `${daysOnMarket} days on market`}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        {listing && <ListingAgentInformation listing={listing} />}
      </Flex>
      {!!propertyDetails && !isViewOnly && (
        <EditOffMarketListingModal
          isOpen={isOpen}
          propertyDetails={propertyDetails}
          onClose={onClose}
          onSave={() =>
            void (async () => {
              await refetchPropertyDetails();
              await refetchListingReport();
            })()
          }
        />
      )}
    </Box>
  );
};
